import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { List, ListItem, ListItemText, Box, IconButton, Menu, MenuItem, ListItemIcon } from '@material-ui/core';
import { KeyboardArrowDown } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import NAVIGATION_ITEMS, { NavItem } from '../../../navigation/comp-layout-sidebar-nav';
import { useSidebar } from '../../../hooks/useSidebar';
import { useRegionSettings } from '../../../providers/RegionSettingsProvider';
import { useFetchMe } from '../../../hooks/userHooks';
import { useAuth } from '../../../hooks/authHooks';
import { signOut } from '../../../services/firebase';
import ChangePasswordDialog from '../../auth/ChangePasswordDialog';
import LanguageSwitcher from '../../../i18n/LanguageSwitcher';
import { useFetchSites } from '../../../hooks/siteHooks';
import { getCurrentRegion, getRegionAdminUrl } from '../../../helpers/siteHelpers';
import { API_BASE_URL } from '../../../config/api';
import { BUILD_INFO } from '../../../config/buildInfo';
import {
    LayoutDashboard,
    ClipboardList,
    LayoutGrid,
    Settings,
    Tag,
    ClipboardCheck,
    Package,
    Calculator,
    Users,
    Sparkles,
    Cog,
    ChevronDown,
    History,
    Store,
    ShoppingCart,
    LifeBuoy,
    GitBranch,
    Boxes,
    Award,
    HandCoins,
    BookOpen,
    GraduationCap,
    ShieldAlert,
    PanelLeftClose,
    PanelLeftOpen,
    Clock,
    Handshake,
    Share2,
    MapPin,
    Gauge,
    Target,
    MessageSquare,
    Inbox,
    Bell,
    Nfc,
    Receipt,
    Gift,
    Webhook,
    Languages,
    Copy,
    ListChecks,
    Briefcase,
    Warehouse,
    UserSearch,
    Library,
    Bot,
    UserCog,
    Tags,
    Printer,
} from 'lucide-react';
import { switchableRegions } from '../../../helpers/regionSwitching';
import { MYSTERY_NAV_ID, MysteryTeamNavOption, withMysteryTeamItems } from './mysteryTeamNav';
import { useMysteryTeam } from '../../../providers/MysteryTeamProvider';
import { ALL_MYSTERY_TEAMS } from '../../../helpers/mysteryTeamHeader';

/**
 * Filter navigation items by feature flags and role.
 * - If a nav item has a featureFlag, it only shows when that flag is true in region settings
 * - If a nav item has a requiredRole, it only shows for users with that role
 * - Children are filtered recursively; parents with no visible children lose their children array
 * - A group with no visible children is dropped, and its landing path becomes its first visible child
 */
export function filterNavItems(
    items: NavItem[],
    regionSettings: Record<string, any> | null,
    userRole: string | null
): NavItem[] {
    const roleLc = userRole ? userRole.toLowerCase() : null;
    const isSuperAdmin = roleLc === 'super admin';
    return items
        .filter((item) => {
            if (item.featureFlag && (!regionSettings || !regionSettings[item.featureFlag])) {
                return false;
            }
            // Super Admin always passes role gates. Otherwise the user's role must be in
            // the allowed set (requiredRoles) or match requiredRole.
            const allowedRoles = item.requiredRoles
                ? item.requiredRoles.map((r) => r.toLowerCase())
                : item.requiredRole
                ? [item.requiredRole.toLowerCase()]
                : null;
            if (allowedRoles && !isSuperAdmin && (!roleLc || !allowedRoles.includes(roleLc))) {
                return false;
            }
            return true;
        })
        .flatMap((item) => {
            if (!item.children) return [item];
            const filteredChildren = filterNavItems(item.children, regionSettings, userRole);
            if (item.group) {
                // A bare group header leads nowhere, and its default landing page may be one this
                // user cannot open: the rail's click goes to the first page they can see instead.
                return filteredChildren.length > 0
                    ? [{ ...item, path: filteredChildren[0].path, children: filteredChildren }]
                    : [];
            }
            return [{ ...item, children: filteredChildren.length > 0 ? filteredChildren : undefined }];
        });
}

// Groups first, then their pages in menu order. Every page has one, as every row inside a Hub
// group does.
const NAV_ICONS: Record<string, any> = {
    dashboard: LayoutDashboard,
    'group-orders': ShoppingCart,
    'group-grading': ClipboardCheck,
    'group-catalog': Library,
    'group-supplies': Warehouse,
    'group-team': Users,
    'group-business': Briefcase,
    support: LifeBuoy,
    'group-settings': Settings,
    orders: ClipboardList,
    'overdue-orders': Clock,
    'mystery-orders': Sparkles,
    'grading-standards': Award,
    'calibration': Target,
    'authentication': ShieldAlert,
    labelling: Tag,
    'grade-check': Calculator,
    catalog: LayoutGrid,
    'catalog-overview': Gauge,
    automated: Bot,
    'catalog-checker': ListChecks,
    'catalog-duplicates': Copy,
    'multi-language': Languages,
    'set-name-translations': Languages,
    'equipment': Boxes,
    'stock': Package,
    'onboarding': BookOpen,
    'new-grader': GraduationCap,
    'royalties': HandCoins,
    'partners': Handshake,
    'marketing': Share2,
    'hq-chat': MessageSquare,
    requests: Inbox,
    notifications: Bell,
    'nfc-setup': Nfc,
    user: UserCog,
    'storefront-contact': MapPin,
    'installed-stores': Store,
    'audit-history': History,
    'region-settings': Cog,
    'workflow-editor': GitBranch,
    'shopify-variants': Tags,
    'webhook-settings': Webhook,
    'customer-lookup': UserSearch,
    label: Printer,
    purchaseDetail: Receipt,
    loyaltyDetail: Gift,
};

const POPUP_STYLE_WITH_CHILDREN = {
    position: 'absolute' as const,
    left: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginLeft: 8,
    zIndex: 1000,
    background: 'linear-gradient(180deg, rgb(var(--pcg-gold)) 0%, rgb(var(--pcg-gold-muted)) 100%)',
    borderRadius: 12,
    minWidth: 160,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    pointerEvents: 'auto' as const,
};

const POPUP_STYLE_WITHOUT_CHILDREN = {
    position: 'absolute' as const,
    left: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginLeft: 8,
    zIndex: 1000,
    background: 'linear-gradient(180deg, rgb(var(--pcg-gold)) 0%, rgb(var(--pcg-gold-muted)) 100%)',
    borderRadius: 12,
    minWidth: 120,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    pointerEvents: 'auto' as const,
};

const POPUP_LABEL_STYLE = {
    fontSize: 15,
    fontWeight: 700,
    fontFamily: '"Inter", sans-serif',
    color: 'rgb(var(--pcg-content))' as const,
    marginBottom: 6,
    cursor: 'pointer' as const,
    userSelect: 'none' as const,
    padding: '4px 0',
};

const POPUP_CHILD_STYLE = {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: '"Inter", sans-serif',
    color: 'rgb(var(--pcg-border))' as const,
    cursor: 'pointer' as const,
    padding: '4px 0',
    userSelect: 'none' as const,
};

const REGION_LABEL_KEYS: Record<string, string> = {
    us: 'sidebar.regions.us',
    nz: 'sidebar.regions.nz',
    au: 'sidebar.regions.au',
    my: 'sidebar.regions.my',
    eu: 'sidebar.regions.eu',
    uk: 'sidebar.regions.uk',
    jp: 'sidebar.regions.jp',
    ph: 'sidebar.regions.ph',
    vn: 'sidebar.regions.vn',
    ca: 'sidebar.regions.ca',
    th: 'sidebar.regions.th',
    events: 'sidebar.regions.events',
    dev: 'sidebar.regions.dev',
};

const REGION_FLAGS: Record<string, string> = {
    us: '/images/USA_Flag.png',
    nz: '/images/NZ_Flag.png',
    au: '/images/AUS_Flag.jpeg',
    my: '/images/MY_Flag.jpeg',
    eu: '/images/DE_Flag.svg',
    uk: '/images/UK_Flag.png',
    jp: '/images/JP_Flag.png',
    ph: '/images/PH_Flag.webp',
    vn: '/images/VN_Flag.webp',
    ca: '/images/CA_Flag.svg',
    th: '/images/TH_Flag.svg',
};

const matchesPath = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`);

const isNavItemActive = (pathname: string, item: NavItem): boolean => {
    const activePaths = item.activePaths && item.activePaths.length ? item.activePaths : [item.path];
    if (activePaths.some(path => matchesPath(pathname, path))) return true;
    // A group is active on any page inside it, without a hand-kept list of its children's paths.
    return !!item.group && !!item.children?.some(child => isNavItemActive(pathname, child));
};

// These helpers used to read a module-level `typedNavigationItems`, but that's now
// dynamic (filtered by region settings + role), so they take the items as an arg.
const getInitialOpenMenus = (pathname: string, items: NavItem[]) =>
    items.reduce<Record<string, boolean>>((acc, item) => {
        if (!item.children?.length) return acc;
        const hasActiveChild = item.children.some(child => isNavItemActive(pathname, child));
        if (hasActiveChild) acc[item.id] = true;
        return acc;
    }, {});

/*
  Groups open and close independently, as in the Hub, and the choice sticks per browser. The
  group holding the current page always opens, whatever was saved: a highlighted page hidden
  behind a closed header is the one state the menu must never show.
*/
const groupOpenKey = (id: string) => `pcg.admin.sidebar.group.${id}`;

const getSavedOpenMenus = (items: NavItem[]) =>
    items.reduce<Record<string, boolean>>((acc, item) => {
        if (!item.children?.length) return acc;
        try {
            const saved = window.localStorage.getItem(groupOpenKey(item.id));
            if (saved != null) acc[item.id] = saved === '1';
        } catch {
            // Blocked storage just means the menu starts from the route.
        }
        return acc;
    }, {});

/*
  Third-level submenus (Catalog > Catalog Duplicator > Sets/Products, Orders > Mystery Slabs >
  teams) open on their own, apart from their group: closing the whole of Catalog to reach a
  submenu would be absurd. A submenu holding the current page starts open.
*/
const getInitialOpenSubMenus = (pathname: string, items: NavItem[]) =>
    items.reduce<Record<string, boolean>>((acc, item) => {
        item.children?.forEach(child => {
            if (child.children?.length && child.children.some(gc => isNavItemActive(pathname, gc))) {
                acc[child.id] = true;
            }
        });
        return acc;
    }, {});

function getInitials(name?: string | null, email?: string | null): string {
    if (name) {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return parts[0].slice(0, 2).toUpperCase();
    }
    if (email) return email[0].toUpperCase();
    return '?';
}

const Sidebar: React.FC = () => {
    const { t } = useTranslation('navigation');
    const getRegionLabel = useCallback((region: string, siteLabel?: string): string =>
        siteLabel || (REGION_LABEL_KEYS[region.toLowerCase()] ? t(REGION_LABEL_KEYS[region.toLowerCase()]) : region.toUpperCase()),
        [t]
    );
    const location = useLocation();
    const history = useHistory();
    const { isCollapsed, setIsCollapsed } = useSidebar();
    const { regionSettings } = useRegionSettings();
    const { data: currentUser } = useFetchMe();
    const { user } = useAuth();
    const { data: sites } = useFetchSites();
    const detectedRegion = getCurrentRegion();
    const apiRegion = API_BASE_URL.match(/\/api\/([^/]+)\/v1/)?.[1] ?? null;
    const currentRegion = (detectedRegion === 'dev' ? apiRegion : detectedRegion) ?? 'dev';
    const isSuperAdmin = currentUser?.role?.toLowerCase() === 'super admin';
    const switchableSites = switchableRegions(sites, currentRegion, isSuperAdmin, currentUser?.regions);
    /*
      Most people belong to one region, so there is nowhere for them to switch to. The row still
      earns its place — it says which region you are looking at, which matters when the same
      portal is deployed per country — but it should not offer a control it cannot honour. With
      nothing to switch to it becomes a label: no chevron, no hover, no click.
    */
    const canSwitchRegion = switchableSites.length > 0;
    const currentSite = (sites ?? []).find(s => s.region.trim().toLowerCase() === currentRegion);
    const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);

    // Mystery slab teams sit under Mystery Slabs as its submenu, once the region has teams.
    const { options: teamOptions, selection: teamSelection, teamName, selectTeam } = useMysteryTeam();
    const teamNavOptions = useMemo<MysteryTeamNavOption[]>(
        () => teamOptions.map(value => ({
            value,
            label: value === ALL_MYSTERY_TEAMS
                ? t('sidebar.mysteryTeam.allTeams')
                : teamName(Number(value)) ?? value,
        })),
        [teamOptions, teamName, t]
    );

    const typedNavigationItems = useMemo(
        () => withMysteryTeamItems(
            filterNavItems(NAVIGATION_ITEMS, regionSettings as any, currentUser?.role || null),
            teamNavOptions
        ),
        [regionSettings, currentUser?.role, teamNavOptions]
    );

    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => ({
        ...getSavedOpenMenus(typedNavigationItems),
        ...getInitialOpenMenus(location.pathname, typedNavigationItems),
    }));
    const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>(() =>
        getInitialOpenSubMenus(location.pathname, typedNavigationItems)
    );
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [hoveredPopupItem, setHoveredPopupItem] = useState<string | null>(null);
    const [hoveredExpandedItem, setHoveredExpandedItem] = useState<string | null>(null);
    const [hoveredExpandedChild, setHoveredExpandedChild] = useState<string | null>(null);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
    const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Merge rather than replace: a group the user opened by hand stays open as they navigate.
        setOpenMenus(prev => ({ ...prev, ...getInitialOpenMenus(location.pathname, typedNavigationItems) }));
        setOpenSubMenus(prev => ({ ...prev, ...getInitialOpenSubMenus(location.pathname, typedNavigationItems) }));
    }, [location.pathname, typedNavigationItems]);

    const handleMouseEnter = useCallback((id: string) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setHoveredItem(id);
    }, []);

    const handleMouseLeave = useCallback(() => {
        hoverTimeoutRef.current = setTimeout(() => setHoveredItem(null), 40);
    }, []);

    const handlePopupItemEnter = useCallback((id: string) => {
        setHoveredPopupItem(id);
    }, []);

    const handlePopupItemLeave = useCallback(() => {
        setHoveredPopupItem(null);
    }, []);

    const handleExpandedChildEnter = useCallback((id: string) => {
        setHoveredExpandedChild(id);
    }, []);

    const handleExpandedChildLeave = useCallback(() => {
        setHoveredExpandedChild(null);
    }, []);

    const handleExpandedItemEnter = useCallback((id: string) => {
        setHoveredExpandedItem(id);
    }, []);

    const handleExpandedItemLeave = useCallback(() => {
        setHoveredExpandedItem(null);
    }, []);

    const handlePopupClick = useCallback((callback?: () => void) => {
        if (callback) callback();
        setHoveredItem(null);
    }, []);

    const toggleMenu = useCallback((id: string, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setOpenMenus(prev => {
            const next = !prev[id];
            try {
                window.localStorage.setItem(groupOpenKey(id), next ? '1' : '0');
            } catch {
                // Non-persistent is fine.
            }
            return { ...prev, [id]: next };
        });
    }, []);

    const handleItemClick = useCallback((item: any) => {
        history.push(item.path);
    }, [history]);

    // Picking a team keeps the user where they are in Mystery Slabs, except on an open card,
    // which may belong to the team they just left.
    const handleTeamPick = useCallback((child: NavItem) => {
        selectTeam(child.mysteryTeam!);
        const path = location.pathname;
        if (!matchesPath(path, child.path) || path.startsWith(`${child.path}/gradings`)) {
            history.push(child.path);
        }
    }, [selectTeam, location.pathname, history]);

    const isChildActive = useCallback((child: NavItem) =>
        child.mysteryTeam != null ? child.mysteryTeam === teamSelection : isNavItemActive(location.pathname, child),
        [teamSelection, location.pathname]
    );

    const handleExpandedItemClick = useCallback((item: NavItem, e: React.MouseEvent) => {
        if (item.children?.length) {
            toggleMenu(item.id, e);
            return;
        }
        handleItemClick(item);
    }, [handleItemClick, toggleMenu]);

    const openPopupChild = useCallback((child: NavItem) => {
        if (child.mysteryTeam != null) handleTeamPick(child);
        else history.push(child.path);
    }, [handleTeamPick, history]);

    const handleToggleSidebar = useCallback(() => {
        setIsCollapsed(!isCollapsed);
    }, [isCollapsed, setIsCollapsed]);



    const initials = getInitials(user?.displayName, user?.email);
    const displayName = user?.displayName || user?.email?.split('@')[0] || t('sidebar.userFallback');
    const email = user?.email || '';
    const photoURL = user?.photoURL || undefined;

    return (
        <>
        <aside
            className="h-full flex flex-col bg-pcg-page text-white shrink-0 border-r border-white/10 transition-all duration-300"
            style={{ width: isCollapsed ? '64px' : '260px' }}
        >
            {/* ── Header ── */}
            <Box
                className={`h-[70px] shrink-0 flex items-center gap-2 border-b border-white/10 ${isCollapsed ? 'px-2 justify-center' : 'px-4 justify-between'}`}
            >
                {!isCollapsed && (
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-pcg-navMuted truncate">
                            {t('sidebar.eyebrow', { defaultValue: 'Admin Portal' })}
                        </div>
                        <div className="text-sm font-semibold mt-0.5 truncate text-pcg-gold">
                            Premier Card Grading
                        </div>
                    </div>
                )}
                {/*
                    The Hub's own control, icon and all: a panel glyph that shows which way the
                    sidebar will go, rather than a back-arrow rotated 180 degrees to mean the
                    opposite of itself.
                */}
                <button
                    type="button"
                    onClick={handleToggleSidebar}
                    className="shrink-0 text-white/40 hover:text-white transition-colors p-1"
                    title={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
                    aria-label={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
                    aria-expanded={!isCollapsed}
                >
                    {isCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
                </button>
            </Box>

            {/* ── EXPANDED VIEW ── */}
            <Box
                className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar transition-opacity duration-200 flex flex-col"
                style={{
                    opacity: isCollapsed ? 0 : 1,
                    pointerEvents: isCollapsed ? 'none' : 'auto',
                    position: isCollapsed ? 'absolute' : 'relative',
                }}
            >
                {/* Office / Region row */}
                <Box className="bg-pcg-page border-b border-white/10">
                    <div
                        className={`px-6 py-3 border-b border-white/10 ${canSwitchRegion ? 'hover:bg-pcg-content cursor-pointer' : ''}`}
                        onClick={canSwitchRegion ? () => setRegionDropdownOpen(prev => !prev) : undefined}
                        role={canSwitchRegion ? 'button' : undefined}
                        tabIndex={canSwitchRegion ? 0 : undefined}
                        aria-expanded={canSwitchRegion ? regionDropdownOpen : undefined}
                        aria-label={canSwitchRegion ? t('sidebar.switchRegion', { defaultValue: 'Switch region' }) : undefined}
                        onKeyDown={canSwitchRegion ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setRegionDropdownOpen(prev => !prev);
                            }
                        } : undefined}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {REGION_FLAGS[currentRegion] && (
                                    <img
                                        src={REGION_FLAGS[currentRegion]}
                                        alt={t('sidebar.regionFlagAlt', { region: getRegionLabel(currentRegion, currentSite?.label) })}
                                        style={{ width: 18, objectFit: 'cover', flexShrink: 0, borderRadius: 2 }}
                                    />
                                )}
                                <span className="text-[13px] font-semibold font-inter text-pcg-navMuted tracking-wide uppercase">
                                    PCG — {getRegionLabel(currentRegion, currentSite?.label)}
                                </span>
                            </div>
                            {canSwitchRegion && (
                                <ChevronDown size={15} style={{ color: 'rgb(var(--pcg-fg) / 0.4)', flexShrink: 0, transform: regionDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            )}
                        </div>
                    </div>

                    {/* Region switcher inline */}
                    {canSwitchRegion && regionDropdownOpen && (
                        <Box style={{ background: 'rgb(var(--pcg-surfaceDeep))' }}>
                            {switchableSites.map(site => (
                                <div
                                    key={site.region}
                                    className="flex items-center gap-3 px-6 py-3 cursor-pointer hover:bg-pcg-content"
                                    onClick={() => {
                                        const siteUrl = getRegionAdminUrl(site.region, site.url);
                                        window.location.href = siteUrl;
                                        setRegionDropdownOpen(false);
                                    }}
                                >
                                    {REGION_FLAGS[site.region.toLowerCase()] && (
                                        <img
                                            src={REGION_FLAGS[site.region.toLowerCase()]}
                                            alt={t('sidebar.regionFlagAlt', { region: site.label })}
                                            style={{ width: 18, objectFit: 'cover', flexShrink: 0, borderRadius: 2 }}
                                        />
                                    )}
                                    <span className="text-[13px] font-semibold font-inter text-white/50 tracking-wide uppercase">
                                        PCG — {getRegionLabel(site.region, site.label)}
                                    </span>
                                </div>
                            ))}
                        </Box>
                    )}

                    {/* Nav items */}
                    <List className="!py-1">
                        {typedNavigationItems.map(item => {
                            const isOpen = !!openMenus[item.id];
                            // As in the Hub, an open group leaves the highlight to the page inside
                            // it; only a closed one holding the current page lights its header.
                            const isActive = isNavItemActive(location.pathname, item) && !(item.children?.length && isOpen);
                            const isHovered = hoveredExpandedItem === item.id;
                            const IconComponent = NAV_ICONS[item.id];

                            return (
                                <Box key={item.id}>
                                    <ListItem
                                        button
                                        aria-expanded={item.children?.length ? isOpen : undefined}
                                        onMouseEnter={() => handleExpandedItemEnter(item.id)}
                                        onMouseLeave={handleExpandedItemLeave}
                                        onClick={(e) => handleExpandedItemClick(item, e)}
                                        style={{
                                            background: isActive
                                                ? 'rgb(var(--pcg-fg) / 0.06)'
                                                : (isHovered ? 'rgb(var(--pcg-fg) / 0.03)' : 'transparent'),
                                            borderLeft: `2px solid ${isActive ? 'rgb(var(--pcg-gold))' : 'transparent'}`,
                                            transition: 'background 0.15s',
                                            padding: '8px 16px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        {IconComponent && (
                                            <IconComponent
                                                size={15}
                                                className="text-white"
                                                style={{
                                                    marginRight: 10,
                                                    flexShrink: 0,
                                                    opacity: isActive ? 1 : 0.6,
                                                }}
                                            />
                                        )}
                                        <ListItemText
                                            primary={t(`nav.${item.id}`, { defaultValue: item.label })}
                                            style={{ margin: 0 }}
                                            primaryTypographyProps={{
                                                style: {
                                                    fontSize: 14,
                                                    color: isActive ? 'rgb(var(--pcg-nav-fg))' : 'rgb(var(--pcg-nav-fg-muted))',
                                                    fontWeight: 400,
                                                    fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                },
                                            }}
                                        />
                                        {item.children && item.children.length ? (
                                            <Box
                                                onClick={(e) => toggleMenu(item.id, e)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                            >
                                                <KeyboardArrowDown
                                                    style={{
                                                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: '0.2s',
                                                        fontSize: 20,
                                                        color: isActive ? 'rgb(var(--pcg-nav-fg))' : 'rgb(var(--pcg-nav-icon))',
                                                    }}
                                                />
                                            </Box>
                                        ) : null}
                                    </ListItem>

                                    {item.children && isOpen && (
                                        <List disablePadding>
                                            {item.children.map(child => {
                                                const childActive = isChildActive(child);
                                                const isChildHovered = hoveredExpandedChild === child.id;
                                                const grandChildren = child.children ?? [];
                                                const isSubOpen = !!openSubMenus[child.id];
                                                const ChildIcon = NAV_ICONS[child.id];
                                                return (
                                                    <Box key={child.id}>
                                                        <ListItem
                                                            button
                                                            aria-expanded={grandChildren.length ? isSubOpen : undefined}
                                                            onClick={() => {
                                                                if (child.mysteryTeam != null) {
                                                                    handleTeamPick(child);
                                                                    return;
                                                                }
                                                                // Mystery Slabs is a page as well as the team submenu:
                                                                // its row opens the page and the teams.
                                                                if (child.id === MYSTERY_NAV_ID && grandChildren.length) {
                                                                    history.push(child.path);
                                                                    setOpenSubMenus(prev => ({ ...prev, [child.id]: true }));
                                                                    return;
                                                                }
                                                                // A submenu's own row opens it rather than navigating: its
                                                                // path is only a default landing page for the collapsed
                                                                // rail, and clicking it here should reveal the submenu.
                                                                if (grandChildren.length) {
                                                                    setOpenSubMenus(prev => ({ ...prev, [child.id]: !prev[child.id] }));
                                                                    return;
                                                                }
                                                                history.push(child.path);
                                                            }}
                                                            onMouseEnter={() => handleExpandedChildEnter(child.id)}
                                                            onMouseLeave={handleExpandedChildLeave}
                                                            style={{
                                                                padding: '7px 20px 7px 32px',
                                                                backgroundColor: childActive
                                                                    ? 'rgb(var(--pcg-fg) / 0.06)'
                                                                    : (isChildHovered ? 'rgb(var(--pcg-fg) / 0.04)' : 'transparent'),
                                                                transition: 'background-color 0.15s ease',
                                                                borderLeft: childActive ? '2px solid rgb(var(--pcg-gold))' : '2px solid transparent',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                            }}
                                                        >
                                                            {/* Indented under its group, icon and all, as in the Hub. */}
                                                            {ChildIcon && (
                                                                <ChildIcon
                                                                    size={14}
                                                                    className="text-white"
                                                                    style={{ marginRight: 10, flexShrink: 0, opacity: childActive ? 1 : 0.55 }}
                                                                />
                                                            )}
                                                            <ListItemText
                                                                primary={t(`nav.${child.id}`, { defaultValue: child.label })}
                                                                style={{ margin: 0 }}
                                                                primaryTypographyProps={{
                                                                    style: {
                                                                        fontSize: 13,
                                                                        color: childActive ? 'rgb(var(--pcg-gold))' : 'rgb(var(--pcg-nav-fg-muted))',
                                                                        fontWeight: childActive ? 600 : 400,
                                                                        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                                    },
                                                                }}
                                                            />
                                                            {grandChildren.length > 0 && (
                                                                <KeyboardArrowDown
                                                                    style={{
                                                                        transform: isSubOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                        transition: '0.2s',
                                                                        fontSize: 18,
                                                                        color: childActive ? 'rgb(var(--pcg-gold))' : 'rgb(var(--pcg-nav-icon))',
                                                                    }}
                                                                />
                                                            )}
                                                        </ListItem>

                                                        {grandChildren.length > 0 && isSubOpen && (
                                                            <List disablePadding>
                                                                {grandChildren.map(grandChild => {
                                                                    const isGrandActive = isChildActive(grandChild);
                                                                    const isGrandHovered = hoveredExpandedChild === grandChild.id;
                                                                    return (
                                                                        <ListItem
                                                                            key={grandChild.id}
                                                                            button
                                                                            onClick={() => openPopupChild(grandChild)}
                                                                            onMouseEnter={() => handleExpandedChildEnter(grandChild.id)}
                                                                            onMouseLeave={handleExpandedChildLeave}
                                                                            style={{
                                                                                padding: '6px 20px 6px 58px',
                                                                                backgroundColor: isGrandActive
                                                                                    ? 'rgb(var(--pcg-fg) / 0.06)'
                                                                                    : (isGrandHovered ? 'rgb(var(--pcg-fg) / 0.04)' : 'transparent'),
                                                                                transition: 'background-color 0.15s ease',
                                                                                borderLeft: isGrandActive ? '2px solid rgb(var(--pcg-gold))' : '2px solid transparent',
                                                                            }}
                                                                        >
                                                                            <ListItemText
                                                                                primary={t(`nav.${grandChild.id}`, { defaultValue: grandChild.label })}
                                                                                style={{ margin: 0 }}
                                                                                primaryTypographyProps={{
                                                                                    style: {
                                                                                        fontSize: 12.5,
                                                                                        color: isGrandActive ? 'rgb(var(--pcg-gold))' : 'rgb(var(--pcg-nav-fg-muted))',
                                                                                        fontWeight: isGrandActive ? 600 : 400,
                                                                                        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                                                                    },
                                                                                }}
                                                                            />
                                                                        </ListItem>
                                                                    );
                                                                })}
                                                            </List>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </List>
                                    )}
                                </Box>
                            );
                        })}
                    </List>
                </Box>

                {/* Spacer */}
                <div className="flex-1" />

                {/* ── User Panel ── */}
                <div
                    className="border-t border-white/10 px-4 py-3 cursor-pointer hover:bg-pcg-content transition-colors"
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                >
                    <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div
                            className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-pcg-page overflow-hidden"
                            style={{ background: 'linear-gradient(180deg, rgb(var(--pcg-gold)) 0%, rgb(var(--pcg-gold-muted)) 100%)' }}
                        >
                            {photoURL
                                ? <img src={photoURL} alt={displayName} className="w-full h-full object-cover" />
                                : initials
                            }
                        </div>

                        {/* Name + email */}
                        <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-white leading-tight truncate">{displayName}</p>
                            {email && <p className="text-[11px] text-white/40 leading-tight truncate">{email}</p>}
                        </div>
                    </div>
                </div>

                {/* ── Build info ── */}
                <div
                    className="px-4 py-2 border-t border-white/10 text-[10px] text-white/30 font-mono leading-tight"
                    title={`Built ${BUILD_INFO.buildTime}`}
                >
                    build {BUILD_INFO.commit}
                </div>
            </Box>

            {/* ── COLLAPSED ICON-ONLY VIEW ── */}
            {isCollapsed && (
                <Box className="flex-1 flex flex-col pt-2">
                    {/* Office flag */}
                    <Box
                        className="flex items-center justify-center py-3 cursor-pointer hover:bg-pcg-content"
                        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.10)', marginBottom: 4 }}
                    >
                        {/* The region's own flag. This was hard-coded to NZ_Flag.png, so every
                            office in the world saw a New Zealand flag once the sidebar was
                            collapsed; the expanded view three hundred lines up had it right. */}
                        {REGION_FLAGS[currentRegion] && (
                            <img
                                src={REGION_FLAGS[currentRegion]}
                                alt={t('sidebar.regionFlagAlt', { region: getRegionLabel(currentRegion, currentSite?.label) })}
                                style={{ width: 32, height: 32, objectFit: 'contain' }}
                            />
                        )}
                    </Box>

                    {/* Collapsed nav icons with hover popup */}
                    {typedNavigationItems.map(item => {
                        const isActive = isNavItemActive(location.pathname, item);
                        const isHovered = hoveredItem === item.id;
                        const hasChildren = item.children && item.children.length > 0;
                        const IconComponent = NAV_ICONS[item.id];

                        return (
                            <Box
                                key={item.id}
                                onMouseEnter={() => handleMouseEnter(item.id)}
                                onMouseLeave={handleMouseLeave}
                                className="relative w-full"
                            >
                                <Box
                                    onClick={() => handlePopupClick(() => history.push(item.path))}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: 46,
                                        width: '100%',
                                        cursor: 'pointer',
                                        background: isActive
                                            ? 'linear-gradient(180deg, rgb(var(--pcg-gold)) 0%, rgb(var(--pcg-gold-muted)) 100%)'
                                            : 'transparent',
                                        transition: 'background 0.15s',
                                    }}
                                    className={!isActive ? 'hover:bg-pcg-content' : ''}
                                >
                                    {IconComponent ? (
                                        <IconComponent
                                            size={20}
                                            className="text-white"
                                            style={{ opacity: isActive ? 1 : 0.65 }}
                                        />
                                    ) : (
                                        <span style={{ color: 'rgb(var(--pcg-fg))', fontSize: 13, fontWeight: 600 }}>{t(`nav.${item.id}`, { defaultValue: item.label })[0]}</span>
                                    )}
                                </Box>

                                {/* Hover popup — with children */}
                                {isHovered && hasChildren && (
                                    <Box
                                        onMouseEnter={() => handleMouseEnter(item.id)}
                                        onMouseLeave={handleMouseLeave}
                                        style={POPUP_STYLE_WITH_CHILDREN}
                                    >
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            style={{
                                                ...POPUP_LABEL_STYLE,
                                                backgroundColor: hoveredPopupItem === item.id ? 'rgba(0,0,0,0.15)' : 'transparent',
                                                transition: 'background-color 0.15s ease',
                                                padding: '8px',
                                            }}
                                            onMouseEnter={() => handlePopupItemEnter(item.id)}
                                            onMouseLeave={handlePopupItemLeave}
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePopupClick(() => history.push(item.path)); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePopupClick(() => history.push(item.path)); } }}
                                        >
                                            {t(`nav.${item.id}`, { defaultValue: item.label })}
                                        </div>
                                        {item.children!.map(child => (
                                            <React.Fragment key={child.id}>
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePopupClick(() => openPopupChild(child)); }}
                                                    onMouseEnter={() => handlePopupItemEnter(child.id)}
                                                    onMouseLeave={handlePopupItemLeave}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePopupClick(() => openPopupChild(child)); } }}
                                                    style={{
                                                        ...POPUP_CHILD_STYLE,
                                                        // The picked team, so the rail says which one is in use.
                                                        ...(child.mysteryTeam != null && child.mysteryTeam === teamSelection
                                                            ? { color: 'rgb(var(--pcg-gold))', fontWeight: 600 }
                                                            : {}),
                                                        backgroundColor: hoveredPopupItem === child.id ? 'rgba(0,0,0,0.15)' : 'transparent',
                                                        transition: 'background-color 0.15s ease',
                                                        padding: '8px',
                                                    }}
                                                >
                                                    {t(`nav.${child.id}`, { defaultValue: child.label })}
                                                </div>
                                                {/* Third level: shown inline and indented — the rail has no room for a
                                                    second flyout, and a group with two entries is cheaper to list than
                                                    to hide behind another hover target. */}
                                                {child.children?.map(grandChild => (
                                                    <div
                                                        key={grandChild.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePopupClick(() => openPopupChild(grandChild)); }}
                                                        onMouseEnter={() => handlePopupItemEnter(grandChild.id)}
                                                        onMouseLeave={handlePopupItemLeave}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePopupClick(() => openPopupChild(grandChild)); } }}
                                                        style={{
                                                            ...POPUP_CHILD_STYLE,
                                                            fontSize: 12,
                                                            // The picked team, so the rail says which one is in use.
                                                            ...(grandChild.mysteryTeam != null && grandChild.mysteryTeam === teamSelection
                                                                ? { color: 'rgb(var(--pcg-gold))', fontWeight: 600 }
                                                                : {}),
                                                            backgroundColor: hoveredPopupItem === grandChild.id ? 'rgba(0,0,0,0.15)' : 'transparent',
                                                            transition: 'background-color 0.15s ease',
                                                            padding: '6px 8px 6px 20px',
                                                        }}
                                                    >
                                                        {t(`nav.${grandChild.id}`, { defaultValue: grandChild.label })}
                                                    </div>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                )}

                                {/* Hover popup — no children */}
                                {isHovered && !hasChildren && (
                                    <Box
                                        onMouseEnter={() => handleMouseEnter(item.id)}
                                        onMouseLeave={handleMouseLeave}
                                        style={POPUP_STYLE_WITHOUT_CHILDREN}
                                    >
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePopupClick(() => history.push(item.path)); }}
                                            onMouseEnter={() => handlePopupItemEnter(item.id)}
                                            onMouseLeave={handlePopupItemLeave}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePopupClick(() => history.push(item.path)); } }}
                                            style={{
                                                ...POPUP_LABEL_STYLE,
                                                backgroundColor: hoveredPopupItem === item.id ? 'rgba(0,0,0,0.15)' : 'transparent',
                                                transition: 'background-color 0.15s ease',
                                                padding: '8px',
                                            }}
                                        >
                                            {t(`nav.${item.id}`, { defaultValue: item.label })}
                                        </div>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Collapsed user avatar (click for menu) */}
                    <div className="border-t border-white/10 py-3 flex flex-col items-center gap-2">
                        <button
                            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                            title={displayName}
                            className="w-8 h-8 rounded-full overflow-hidden cursor-pointer"
                            style={{ background: 'linear-gradient(180deg, rgb(var(--pcg-gold)) 0%, rgb(var(--pcg-gold-muted)) 100%)', border: 'none', padding: 0 }}
                        >
                            <div className="w-full h-full flex items-center justify-center text-[11px] font-bold text-pcg-page">
                                {photoURL
                                    ? <img src={photoURL} alt={displayName} className="w-full h-full object-cover" />
                                    : initials
                                }
                            </div>
                        </button>
                        <span
                            className="text-[9px] text-white/25 font-mono"
                            title={`built ${BUILD_INFO.buildTime}`}
                        >
                            {BUILD_INFO.commit}
                        </span>
                    </div>
                </Box>
            )}
        </aside>
        <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            getContentAnchorEl={null}
        >
            <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px 10px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <LanguageSwitcher tone="light" />
            </div>
            <MenuItem
                onClick={() => {
                    setUserMenuAnchor(null);
                    setChangePasswordOpen(true);
                }}
            >
                <ListItemIcon style={{ minWidth: 32 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                </ListItemIcon>
                <ListItemText primary={t('sidebar.changePassword')} />
            </MenuItem>
            <MenuItem
                onClick={() => {
                    setUserMenuAnchor(null);
                    signOut();
                }}
            >
                <ListItemIcon style={{ minWidth: 32 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                </ListItemIcon>
                <ListItemText primary={t('sidebar.signOut')} />
            </MenuItem>
        </Menu>
        <ChangePasswordDialog
            open={changePasswordOpen}
            onClose={() => setChangePasswordOpen(false)}
        />
        </>
    );
};

export default Sidebar;
